import { KeyboardEvent, MouseEvent, useCallback, useMemo } from 'react'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import { HugeiconsIcon } from '@hugeicons/react'
import { useLocation } from 'wouter'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  Delete01Icon,
  Invoice01Icon,
  UserCheck01Icon,
  UserRemove01Icon
} from '@hugeicons/core-solid-rounded'

import * as Table from '@/components/ui/Table'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useStateToLabel } from '../utils/helper'

export default function StayRow({
  stay,
  showUpdate = false,
  showDelete = false,
  onDelete
}: {
  stay: GraphQL.StayFragmentFragment
  showUpdate?: boolean
  showDelete?: boolean
  onDelete: () => void
}) {
  const { t } = useLingui()
  const stateToLabel = useStateToLabel()
  const [, navigate] = useLocation()

  const onEdit = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      event.stopPropagation()

      navigate(`/stay/${stay.token}`)
    },
    [navigate, stay]
  )

  const start = useMemo(
    () => new Date(stay.scheduledStart),
    [stay.scheduledStart]
  )
  const stop = useMemo(() => new Date(stay.scheduledStop), [stay.scheduledStop])

  const onCheckIn = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      navigate(`/stay/${stay.token}`, { state: { isCheckIn: true } })
    },
    [stay.token, navigate]
  )

  const onCheckOut = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      navigate(`/stay/${stay.token}`, { state: { isCheckOut: true } })
    },
    [stay.token, navigate]
  )

  const onShowOrders = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      navigate(`/stay-orders/${stay.token}`)
    },
    [stay.token, navigate]
  )

  const space = useMemo(
    () => getFragment(SharedAPI.space.SPACE_FRAGMENT, stay.space),
    [stay.space]
  )

  return (
    <Table.Row className="cursor-pointer" onClick={onEdit} onEnter={onEdit}>
      <Table.Cell>
        <span className="text-text">
          {space ? (
            <>
              <span>{space?.identifier}</span>{' '}
              <span className="text-text-light">({space?.name})</span>
            </>
          ) : (
            <Trans>No room</Trans>
          )}
        </span>
      </Table.Cell>
      <Table.Cell>{dateTime.fullFormatter.format(start)}</Table.Cell>
      <Table.Cell>{dateTime.fullFormatter.format(stop)}</Table.Cell>
      <Table.Cell>{stay.guests?.length}</Table.Cell>
      <Table.Cell>{stateToLabel[stay.state]}</Table.Cell>
      <Table.Cell>
        <div className="flex flex-row items-center gap-5">
          {stay.state !== GraphQL.StayStates.Scheduled && (
            <button
              tabIndex={0}
              aria-label="orders"
              onClick={onShowOrders}
              title={t`Show Orders`}
            >
              <HugeiconsIcon icon={Invoice01Icon} className="size-5" />
            </button>
          )}
          {stay.state === GraphQL.StayStates.Scheduled && (
            <div className="size-5" />
          )}
          {showUpdate && stay.state === GraphQL.StayStates.Scheduled && (
            <button
              tabIndex={0}
              onClick={onCheckIn}
              aria-label={t`Check in`}
              title={t({
                comment: 'Label for the action of checking in',
                message: 'Check in'
              })}
            >
              <HugeiconsIcon icon={UserCheck01Icon} className="size-5" />
            </button>
          )}
          {showUpdate && stay.state === GraphQL.StayStates.Staying && (
            <button
              tabIndex={0}
              onClick={onCheckOut}
              aria-label={t`Check out`}
              title={t({
                comment: 'Label for the action of checking out',
                message: 'Check out'
              })}
            >
              <HugeiconsIcon icon={UserRemove01Icon} className="size-5" />
            </button>
          )}
          {stay.state === GraphQL.StayStates.CheckedOut && (
            <div className="size-5" />
          )}
          {showDelete && (
            <ConfirmDialog
              title={t`Delete Stay`}
              message={t`Are your sure that you want to delete the stay, this cannot be reversed?`}
              onConfirm={onDelete}
            >
              <HugeiconsIcon icon={Delete01Icon} className="size-5" />
            </ConfirmDialog>
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
